import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { TopBar } from '@/components/layout/TopBar';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { toast } from '@/hooks/use-toast';
import { Loader2, ShieldCheck, ShieldOff, Search, AlertTriangle } from 'lucide-react';

/**
 * BJ-GAP-002 — govern the designated support-operator set.
 *
 * CORE-BJ recorded that "the most security-sensitive list in the platform is
 * the one with the least governed change path". This is that path.
 *
 * The separation this page embodies: `internal_admins` GOVERN the set;
 * `support_operator_grants` IS the capability. An internal admin without a
 * grant still cannot enter Support Mode, and a support operator cannot grant
 * support to anyone. Everything here goes through the server-authoritative
 * RPC — this page holds no authority of its own.
 */

interface OperatorRow {
  grant_id: string;
  user_id: string;
  user_email: string | null;
  display_name: string | null;
  granted_at: string;
  granted_by: string | null;
  granted_by_email: string | null;
  grant_reason: string | null;
  revoked_at: string | null;
  revoked_by: string | null;
  revoked_by_email: string | null;
  revoke_reason: string | null;
  is_effective: boolean;
}

interface Candidate {
  user_id: string;
  user_email: string | null;
  display_name: string | null;
  email_confirmed: boolean;
  already_support_operator: boolean;
}

export default function SupportOperatorsAdmin() {
  const queryClient = useQueryClient();
  const [showRevoked, setShowRevoked] = useState(false);

  // ── grant flow ──
  const [email, setEmail] = useState('');
  const [resolving, setResolving] = useState(false);
  const [candidate, setCandidate] = useState<Candidate | null>(null);
  const [grantReason, setGrantReason] = useState('');
  const [granting, setGranting] = useState(false);

  // ── revoke flow ──
  const [revokeTarget, setRevokeTarget] = useState<OperatorRow | null>(null);
  const [revokeReason, setRevokeReason] = useState('');
  const [revoking, setRevoking] = useState(false);

  const { data: operators, isLoading, isError } = useQuery({
    queryKey: ['support-operators', showRevoked],
    queryFn: async (): Promise<OperatorRow[]> => {
      const { data, error } = await (supabase as any)
        .rpc('admin_list_support_operators', { p_include_revoked: showRevoked });
      if (error) throw error;
      return (data ?? []) as OperatorRow[];
    },
  });

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['support-operators'] });

  const handleResolve = async () => {
    const trimmed = email.trim();
    if (!trimmed) return;
    setResolving(true);
    setCandidate(null);
    try {
      const { data, error } = await (supabase as any)
        .rpc('admin_resolve_support_candidate', { p_email: trimmed });
      if (error) throw error;
      const found = (data ?? [])[0] as Candidate | undefined;
      if (!found) {
        // Deliberately does NOT pre-authorise a future signup.
        toast({
          title: 'No account found',
          description:
            'Support can only be granted to an existing OptiBay account. Ask the person to sign in once first.',
          variant: 'destructive',
        });
        return;
      }
      setCandidate(found);
    } catch (e: any) {
      toast({ title: 'Lookup failed', description: e?.message ?? 'Unknown error', variant: 'destructive' });
    } finally {
      setResolving(false);
    }
  };

  const handleGrant = async () => {
    if (!candidate || !grantReason.trim()) return;
    setGranting(true);
    try {
      const { data, error } = await (supabase as any).rpc('admin_support_operator_action', {
        p_target_user_id: candidate.user_id,
        p_action: 'GRANT',
        p_reason: grantReason.trim(),
      });
      if (error) throw error;
      toast({
        title: data?.decision === 'already_granted' ? 'Already a support operator' : 'Support access granted',
        description: `${candidate.user_email ?? candidate.user_id} can now enter Support Mode.`,
      });
      setCandidate(null); setEmail(''); setGrantReason('');
      refresh();
    } catch (e: any) {
      toast({ title: 'Grant failed', description: e?.message ?? 'Unknown error', variant: 'destructive' });
    } finally {
      setGranting(false);
    }
  };

  const handleRevoke = async () => {
    if (!revokeTarget || !revokeReason.trim()) return;
    setRevoking(true);
    try {
      const { data, error } = await (supabase as any).rpc('admin_support_operator_action', {
        p_target_user_id: revokeTarget.user_id,
        p_action: 'REVOKE',
        p_reason: revokeReason.trim(),
      });
      if (error) throw error;
      const closed = data?.sessions_closed ?? 0;
      toast({
        title: data?.decision === 'already_revoked' ? 'Already revoked' : 'Support access revoked',
        description: closed > 0
          ? `${closed} active support session${closed === 1 ? '' : 's'} ended immediately.`
          : 'No active support session was open.',
      });
      setRevokeTarget(null); setRevokeReason('');
      refresh();
    } catch (e: any) {
      toast({ title: 'Revoke failed', description: e?.message ?? 'Unknown error', variant: 'destructive' });
    } finally {
      setRevoking(false);
    }
  };

  const effective = (operators ?? []).filter(o => o.is_effective);
  const history = (operators ?? []).filter(o => !o.is_effective);

  return (
    <div className="flex flex-col h-screen bg-background">
      <TopBar />
      <main className="flex-1 overflow-auto">
        <div className="max-w-4xl mx-auto p-6 space-y-6">
          <div>
            <h1 className="text-3xl font-bold">Support Operators</h1>
            <p className="text-muted-foreground mt-1">
              People designated to enter customer shops and accounts.
            </p>
          </div>

          <Alert>
            <ShieldCheck className="h-4 w-4" />
            <AlertDescription>
              Being a platform administrator does <strong>not</strong> grant Support Mode. Support
              access is an explicit designation, and it must be granted here — including to yourself.
            </AlertDescription>
          </Alert>

          {/* ── designate ── */}
          <Card>
            <CardHeader>
              <CardTitle>Designate a support operator</CardTitle>
              <CardDescription>
                Support can only be granted to an existing account. The designation is stored
                against that person, not their email address.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex gap-2">
                <Input
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); setCandidate(null); }}
                  placeholder="person@optibay.example"
                  aria-label="Account email"
                  data-testid="support-operator-email"
                />
                <Button onClick={handleResolve} disabled={!email.trim() || resolving} variant="outline">
                  {resolving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                  <span className="ml-2">Find</span>
                </Button>
              </div>

              {candidate && (
                <div className="space-y-3 rounded-md border border-border p-3" data-testid="support-operator-candidate">
                  <div className="text-sm">
                    <p className="font-medium">{candidate.display_name ?? candidate.user_email}</p>
                    <p className="text-xs text-muted-foreground">{candidate.user_email}</p>
                    {candidate.already_support_operator && (
                      <Badge variant="secondary" className="mt-2">Already a support operator</Badge>
                    )}
                  </div>
                  <Textarea
                    value={grantReason}
                    onChange={(e) => setGrantReason(e.target.value)}
                    rows={2}
                    placeholder="Why does this person need support access? (recorded, internal)"
                    aria-label="Governance reason"
                    data-testid="support-operator-grant-reason"
                  />
                  <p className="text-xs text-muted-foreground">
                    This reason is internal governance evidence. It is never shown to customers.
                  </p>
                  <Button
                    onClick={handleGrant}
                    disabled={!grantReason.trim() || granting}
                    data-testid="support-operator-grant-confirm"
                  >
                    {granting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Grant support access
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* ── current set ── */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Current support operators</CardTitle>
                <CardDescription>{effective.length} designated</CardDescription>
              </div>
              <Button variant="outline" size="sm" onClick={() => setShowRevoked(v => !v)}>
                {showRevoked ? 'Hide history' : 'Show history'}
              </Button>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="flex justify-center py-4"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
              ) : isError ? (
                <Alert variant="destructive" data-testid="support-operator-list-error">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription>Could not load support operators.</AlertDescription>
                </Alert>
              ) : effective.length === 0 ? (
                <p className="text-sm text-muted-foreground" data-testid="support-operator-empty">
                  No support operators are designated. Nobody can currently enter Support Mode.
                </p>
              ) : (
                <div className="space-y-2" data-testid="support-operator-list">
                  {effective.map(op => (
                    <div key={op.grant_id} className="flex items-start justify-between gap-3 rounded-md border border-border p-3">
                      <div className="text-sm">
                        <p className="font-medium">{op.display_name ?? op.user_email}</p>
                        <p className="text-xs text-muted-foreground">{op.user_email}</p>
                        <p className="text-xs text-muted-foreground mt-1">
                          Granted {new Date(op.granted_at).toLocaleDateString()}
                          {op.granted_by_email ? ` by ${op.granted_by_email}` : ''}
                          {op.grant_reason ? ` — ${op.grant_reason}` : ''}
                        </p>
                      </div>
                      <Button
                        variant="outline" size="sm"
                        onClick={() => { setRevokeTarget(op); setRevokeReason(''); }}
                      >
                        <ShieldOff className="h-4 w-4 mr-1" /> Revoke
                      </Button>
                    </div>
                  ))}
                </div>
              )}

              {showRevoked && history.length > 0 && (
                <div className="mt-4 space-y-2">
                  <h4 className="text-sm font-medium">Revoked</h4>
                  {history.map(op => (
                    <div key={op.grant_id} className="rounded-md border border-border p-3 text-sm">
                      <p className="font-medium">{op.display_name ?? op.user_email}</p>
                      <p className="text-xs text-muted-foreground">
                        Granted {new Date(op.granted_at).toLocaleDateString()}
                        {op.granted_by_email ? ` by ${op.granted_by_email}` : ''} · revoked{' '}
                        {op.revoked_at ? new Date(op.revoked_at).toLocaleDateString() : ''}
                        {op.revoked_by_email ? ` by ${op.revoked_by_email}` : ''}
                        {op.revoke_reason ? ` — ${op.revoke_reason}` : ''}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>

      {/* ── revoke confirmation ── */}
      <Dialog open={!!revokeTarget} onOpenChange={(o) => { if (!o && !revoking) setRevokeTarget(null); }}>
        <DialogContent data-testid="support-operator-revoke-dialog">
          <DialogHeader>
            <DialogTitle>Revoke support access</DialogTitle>
            <DialogDescription>
              {revokeTarget?.user_email} will lose support access immediately.
            </DialogDescription>
          </DialogHeader>
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              Any support session they currently have open will end at once, and their Support Mode
              view will close.
            </AlertDescription>
          </Alert>
          <Textarea
            value={revokeReason}
            onChange={(e) => setRevokeReason(e.target.value)}
            rows={2}
            placeholder="Why is access being removed? (recorded, internal)"
            aria-label="Revocation reason"
            data-testid="support-operator-revoke-reason"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRevokeTarget(null)} disabled={revoking}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleRevoke}
              disabled={!revokeReason.trim() || revoking}
              data-testid="support-operator-revoke-confirm"
            >
              {revoking && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Revoke access
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
